import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert, Autocomplete, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider,
  FormControl, Grid, IconButton, InputLabel, MenuItem, Select, Step, StepLabel, Stepper,
  TextField, Tooltip, Typography
} from "@mui/material";
import AddIcon from "@mui/icons-material/AddBox";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import { useTranslation } from "react-i18next";
import { currentPeriod, type Credit, type Partner, type PartnerService, type Receipt, type Service } from "@kappa-v2/contracts";
import { useApi } from "../../../hooks/useApi.js";
import { formLabel } from "../../../lib/labels.js";
import { PAYMENT_TYPES, isRecurringService } from "../../../lib/payments.js";
import { ConfirmDialog } from "../../../components/ConfirmDialog.js";
import { CreditEditDialog } from "../../../components/CreditEditDialog.js";
import { CreditCreateDialog } from "../../../components/CreditCreateDialog.js";

// Iscrizione guidata, dopo l'anagrafica (step 0, già salvato): ogni step salva
// subito e si può saltare con "Avanti".
//   1. servizi ricorrenti agganciati al socio (partner-services)
//   2. scheda del mese scelto: genera le quote ricorrenti del solo socio, poi le
//      competenze si possono modificare/aggiungere
//   3. pagamento della scheda
// Si apre dopo la creazione di un socio o, per un socio esistente, dal menu riga.

const eur = (n?: number) => `€ ${(n ?? 0).toLocaleString("it-IT", { minimumFractionDigits: 2 })}`;
const todayISO = () => new Date().toISOString().slice(0, 10);

function shiftPeriod(period: string, months: number): string {
  const [mm, yyyy] = period.split("/").map(Number);
  const d = new Date(yyyy!, mm! - 1 + months, 1);
  return `${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
}

function resolveList<T>(value: T[] | { data?: T[] } | undefined): T[] {
  if (!value) return [];
  return Array.isArray(value) ? value : (value.data ?? []);
}

interface Props {
  open: boolean;
  partner: Partner | null;
  onClose: () => void;
}

export function EnrollmentWizardDialog({ open, partner, onClose }: Props) {
  const { t } = useTranslation();
  const { call } = useApi();
  const navigate = useNavigate();
  const personId = partner?._id ?? "";

  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);

  // Step 1 — servizi ricorrenti
  const [services, setServices] = useState<Service[]>([]);
  const [subs, setSubs] = useState<PartnerService[]>([]);
  const [toAdd, setToAdd] = useState<Service | null>(null);
  const [removeSub, setRemoveSub] = useState<PartnerService | null>(null);

  // Step 2 — scheda del mese
  const periods = useMemo(() => {
    const now = currentPeriod();
    return [-2, -1, 0, 1, 2, 3].map((n) => shiftPeriod(now, n));
  }, []);
  const [period, setPeriod] = useState(currentPeriod());
  const [credits, setCredits] = useState<Credit[]>([]);
  const [receipt, setReceipt] = useState<Receipt | null>(null);
  const [generated, setGenerated] = useState<number | null>(null);
  const [editCredit, setEditCredit] = useState<Credit | null>(null);
  const [addCreditOpen, setAddCreditOpen] = useState(false);
  const [deleteCredit, setDeleteCredit] = useState<Credit | null>(null);

  // Step 3 — pagamento
  const [payment, setPayment] = useState({ debit: 0, datePaid: todayISO(), paymentType: PAYMENT_TYPES[0]! });
  const [paid, setPaid] = useState(false);

  const serviceById = useMemo(() => new Map(services.map((s) => [s._id ?? "", s])), [services]);
  const addable = useMemo(() => {
    const taken = new Set(subs.map((s) => s.serviceId));
    return services.filter((s) => isRecurringService(s) && !taken.has(s._id ?? ""))
      .sort((a, b) => a.name.localeCompare(b.name, "it"));
  }, [services, subs]);
  const remaining = (receipt?.totalCredit ?? 0) - (receipt?.totalDebit ?? 0);

  const loadSubs = useCallback(async () => {
    if (!personId) return;
    setSubs(resolveList(await call<PartnerService[] | { data?: PartnerService[] }>(`/api/v1/payments/partner-services?personId=${personId}`)));
  }, [call, personId]);

  const loadSheet = useCallback(async () => {
    if (!personId) return;
    const qs = `period=${encodeURIComponent(period)}`;
    const [cr, rc] = await Promise.all([
      call<Credit[] | { data?: Credit[] }>(`/api/v1/payments/credits?personId=${personId}&${qs}`),
      call<Receipt[] | { data?: Receipt[] }>(`/api/v1/payments/receipts/partners/${personId}?${qs}`)
    ]);
    const r = resolveList(rc)[0] ?? null;
    setCredits(resolveList(cr));
    setReceipt(r);
    return r;
  }, [call, personId, period]);

  // Apertura: si riparte dallo step 1 con il mese corrente.
  useEffect(() => {
    if (!open || !personId) return;
    setStep(1);
    setPeriod(currentPeriod());
    setGenerated(null);
    setPaid(false);
    setToAdd(null);
    call<Service[] | { data?: Service[] }>("/api/v1/services").then((r) => setServices(resolveList(r))).catch(() => undefined);
    loadSubs().catch(() => undefined);
  }, [open, personId, call, loadSubs]);

  // Scheda e pagamento seguono il mese scelto.
  useEffect(() => {
    if (!open || step < 2) return;
    setGenerated(null);
    loadSheet()
      .then((r) => setPayment((p) => ({ ...p, debit: Math.max((r?.totalCredit ?? 0) - (r?.totalDebit ?? 0), 0) })))
      .catch(() => undefined);
  }, [open, step, loadSheet]);

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    try { await fn(); } catch { /* errore già notificato da useApi */ } finally { setBusy(false); }
  };

  const addService = () => run(async () => {
    if (!toAdd?._id) return;
    await call("/api/v1/payments/partner-services", {
      method: "POST",
      body: {
        personId,
        serviceId: toAdd._id,
        active: true,
        name: toAdd.name,
        description: toAdd.description ?? "",
        emailNotification: Boolean(toAdd.emailNotification),
        isDeducible: Boolean(toAdd.isDeducible),
        serviceType: toAdd.serviceType
      }
    });
    setToAdd(null);
    await loadSubs();
  });

  const confirmRemoveSub = () => run(async () => {
    if (!removeSub?._id) return;
    await call(`/api/v1/payments/partner-services/${removeSub._id}`, { method: "DELETE" });
    setRemoveSub(null);
    await loadSubs();
  });

  const prepareSheet = () => run(async () => {
    const res = await call<{ created: number }>("/api/v1/payments/credits/period", { method: "POST", body: { period, personId } });
    const r = await loadSheet();
    setGenerated(res.created);
    setPayment((p) => ({ ...p, debit: Math.max((r?.totalCredit ?? 0) - (r?.totalDebit ?? 0), 0) }));
  });

  const confirmDeleteCredit = () => run(async () => {
    if (!deleteCredit?._id) return;
    await call(`/api/v1/payments/credits/${deleteCredit._id}`, { method: "DELETE" });
    setDeleteCredit(null);
    await loadSheet();
  });

  const registerPayment = () => run(async () => {
    await call("/api/v1/payments/debits", {
      method: "POST",
      body: {
        personId,
        period,
        debit: Number(payment.debit),
        datePaid: payment.datePaid,
        paymentType: payment.paymentType,
        partnerType: partner?.partnerType ?? "form.partnerType.other",
        paymentNumber: "NEW"
      }
    });
    const r = await loadSheet();
    setPaid(true);
    setPayment((p) => ({ ...p, debit: Math.max((r?.totalCredit ?? 0) - (r?.totalDebit ?? 0), 0) }));
  });

  const refreshSheet = () => { loadSheet().catch(() => undefined); };
  const openReceipt = () => {
    if (!receipt?._id) return;
    onClose();
    navigate(`/protected/receipt/${receipt._id}/partner/${personId}/period/${encodeURIComponent(period)}`);
  };

  const fullName = partner ? `${partner.lastName} ${partner.firstName}` : "";
  const steps = [t("enrollment.steps.partner"), t("enrollment.steps.services"), t("enrollment.steps.sheet"), t("enrollment.steps.payment")];

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>{t("enrollment.title", { name: fullName })}</DialogTitle>
        <DialogContent>
          <Stepper activeStep={step} alternativeLabel sx={{ mb: 3, mt: 1 }}>
            {steps.map((label, i) => (
              <Step key={label} completed={i < step}><StepLabel>{label}</StepLabel></Step>
            ))}
          </Stepper>

          {step === 1 && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{t("enrollment.servicesIntro")}</Typography>
              <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
                <Autocomplete
                  sx={{ flex: 1 }}
                  size="small"
                  options={addable}
                  value={toAdd}
                  onChange={(_, v) => setToAdd(v)}
                  getOptionLabel={(s) => `${s.name} — ${eur(s.price)}`}
                  isOptionEqualToValue={(a, b) => a._id === b._id}
                  renderInput={(params) => <TextField {...params} label={t("enrollment.serviceLabel")} />}
                />
                <Button variant="contained" startIcon={<AddIcon />} disabled={!toAdd || busy} onClick={addService}>
                  {t("common.add")}
                </Button>
              </Box>
              {subs.length === 0 ? (
                <Typography variant="body2" color="text.secondary">{t("enrollment.noServices")}</Typography>
              ) : subs.map((s) => {
                const svc = serviceById.get(s.serviceId);
                return (
                  <Box key={s._id} sx={{ display: "flex", alignItems: "center", py: 1, borderTop: "1px solid rgba(0,0,0,.06)" }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={600}>{s.name || svc?.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {[svc?.paymentFrequency ? formLabel(svc.paymentFrequency) : "", s.description || svc?.description].filter(Boolean).join(" · ")}
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ mx: 2, whiteSpace: "nowrap" }}>{eur(svc?.price)}</Typography>
                    <Tooltip title={t("common.delete")}>
                      <IconButton size="small" onClick={() => setRemoveSub(s)}><DeleteIcon fontSize="small" /></IconButton>
                    </Tooltip>
                  </Box>
                );
              })}
            </Box>
          )}

          {step === 2 && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{t("enrollment.sheetIntro")}</Typography>
              <Box sx={{ display: "flex", gap: 1, mb: 2, flexWrap: "wrap" }}>
                <FormControl size="small" sx={{ minWidth: 140 }}>
                  <InputLabel>{t("common.period")}</InputLabel>
                  <Select label={t("common.period")} value={period} onChange={(e) => setPeriod(e.target.value)}>
                    {periods.map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                  </Select>
                </FormControl>
                <Button variant="contained" disabled={busy} onClick={prepareSheet}>{t("enrollment.prepare")}</Button>
                <Button variant="outlined" startIcon={<AddIcon />} disabled={busy} onClick={() => setAddCreditOpen(true)}>
                  {t("receiptDetail.addCompetence")}
                </Button>
              </Box>
              {generated !== null && (
                <Alert severity={generated > 0 ? "success" : "info"} sx={{ mb: 2 }}>
                  {generated > 0 ? t("enrollment.prepared", { count: generated }) : t("enrollment.nothingGenerated")}
                </Alert>
              )}
              {credits.length === 0 ? (
                <Typography variant="body2" color="text.secondary">{t("enrollment.noCompetences")}</Typography>
              ) : (
                <>
                  {credits.map((c) => (
                    <Box key={c._id} sx={{ display: "flex", alignItems: "center", py: 1, borderTop: "1px solid rgba(0,0,0,.06)" }}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={600}>{c.name}</Typography>
                        {c.description && <Typography variant="caption" color="text.secondary">{c.description}</Typography>}
                      </Box>
                      <Typography variant="body2" fontWeight={600} sx={{ mx: 2, whiteSpace: "nowrap" }}>{eur(c.credit)}</Typography>
                      <Tooltip title={t("common.edit")}>
                        <IconButton size="small" onClick={() => setEditCredit(c)}><EditIcon fontSize="small" /></IconButton>
                      </Tooltip>
                      <Tooltip title={t("common.delete")}>
                        <IconButton size="small" onClick={() => setDeleteCredit(c)}><DeleteIcon fontSize="small" /></IconButton>
                      </Tooltip>
                    </Box>
                  ))}
                  <Divider />
                  <Box sx={{ display: "flex", justifyContent: "flex-end", pt: 1, pr: 11 }}>
                    <Typography variant="body2" fontWeight={700}>{t("common.total")}: {eur(credits.reduce((n, c) => n + c.credit, 0))}</Typography>
                  </Box>
                </>
              )}
            </Box>
          )}

          {step === 3 && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{t("enrollment.paymentIntro", { period })}</Typography>
              {paid && <Alert severity="success" sx={{ mb: 2 }}>{t("enrollment.paymentSaved")}</Alert>}
              {remaining <= 0 ? (
                <Alert severity="info" sx={{ mb: 2 }}>{t("enrollment.nothingToPay")}</Alert>
              ) : (
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <TextField fullWidth size="small" label={`${t("common.amount")} (€)`} type="number"
                      value={payment.debit}
                      onChange={(e) => setPayment((p) => ({ ...p, debit: Number(e.target.value) }))}
                      inputProps={{ step: 0.01, min: 0, max: remaining }} />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField fullWidth size="small" label={t("receiptDetail.paymentDate")} type="date"
                      InputLabelProps={{ shrink: true }}
                      value={payment.datePaid}
                      onChange={(e) => setPayment((p) => ({ ...p, datePaid: e.target.value }))} />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl fullWidth size="small">
                      <InputLabel>{t("receiptDetail.paymentMethod")}</InputLabel>
                      <Select label={t("receiptDetail.paymentMethod")} value={payment.paymentType}
                        onChange={(e) => setPayment((p) => ({ ...p, paymentType: e.target.value }))}>
                        {PAYMENT_TYPES.map((m) => <MenuItem key={m} value={m}>{formLabel(m)}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <Button variant="contained" color="success" disabled={busy || !(payment.debit > 0)} onClick={registerPayment}>
                      {t("receiptDetail.registerPayment")}
                    </Button>
                  </Grid>
                </Grid>
              )}
              <Divider sx={{ mt: 2 }} />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                {t("receiptDetail.summary", { due: eur(receipt?.totalCredit), paid: eur(receipt?.totalDebit), remaining: eur(remaining) })}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          {step === 3 && receipt?._id && <Button onClick={openReceipt} sx={{ mr: "auto" }}>{t("enrollment.openReceipt")}</Button>}
          <Button onClick={onClose}>{t("common.close")}</Button>
          {step > 1 && <Button onClick={() => setStep((s) => s - 1)} disabled={busy}>{t("common.back")}</Button>}
          {step < 3
            ? <Button variant="contained" onClick={() => setStep((s) => s + 1)} disabled={busy}>{t("common.next")}</Button>
            : <Button variant="contained" onClick={onClose} disabled={busy}>{t("enrollment.finish")}</Button>}
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={Boolean(removeSub)}
        message={t("enrollment.removeServiceConfirm", { name: removeSub?.name ?? "" })}
        onConfirm={confirmRemoveSub}
        onCancel={() => setRemoveSub(null)}
      />
      <ConfirmDialog
        open={Boolean(deleteCredit)}
        message={t("receiptDetail.deleteCompetenceConfirm", { name: deleteCredit?.name })}
        onConfirm={confirmDeleteCredit}
        onCancel={() => setDeleteCredit(null)}
      />
      <CreditEditDialog credit={editCredit} onClose={() => setEditCredit(null)} onSaved={refreshSheet} />
      <CreditCreateDialog
        open={addCreditOpen}
        personId={personId}
        period={period}
        partnerType={partner?.partnerType}
        onClose={() => setAddCreditOpen(false)}
        onSaved={refreshSheet}
      />
    </>
  );
}
